import { useParams } from "react-router-dom";
import { Controller } from "react-hook-form";
import Button from "components/button";
import Icon from "components/icon";
import Input from "components/input";
import Select from "components/select";
import Typography from "components/typography";
import ConfirmationModal from "components/shared/confirmation-modal";
import useEditVehicleOperationForm from "./useEditVehicleOperationForm";

export const editVehicleOperationPageRouteName = (id?: string) =>
  `/config/vehicle-operation-config/${id ?? ":id"}/edit`;

const EditVehicleOperationPage = () => {
  const { id } = useParams<{ id: string }>();
  const {
    control,
    handleSubmit,
    register,
    errors,
    showSaveModal,
    setShowSaveModal,
    showCancelModal,
    setShowCancelModal,
    onSubmit,
    handleConfirmSave,
    handleConfirmCancel,
    showAttachment,
    vehicleModelOptions,
    t,
    vehicleTypes,
    tripTypes,
    attachmentOptions,
  } = useEditVehicleOperationForm(id);

  return (
    <div className="flex flex-col gap-6">
      <section className="rounded-2xl border border-border bg-white p-6 shadow-sm">
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col gap-4"
        >
          {/* Vehicle Type & Vehicle Model - inline */}
          <div className="grid grid-cols-2 gap-4">
            <Controller
              name="vehicleTypeId"
              control={control}
              render={({ field, fieldState }) => (
                <div className="flex flex-col gap-1.5">
                  <Typography variant="bodyLarge">
                    {t("label-vehicle-type")}{" "}
                    <span className="text-error-500">*</span>
                  </Typography>
                  <Select
                    options={vehicleTypes.map((vt) => ({
                      label: vt.name ?? "",
                      value: vt.id ?? "",
                    }))}
                    value={field.value}
                    placeholder={t("label-vehicle-type")}
                    onValueChange={(val) => field.onChange(val)}
                    error={fieldState.error}
                    data-testid="dropdown-operation-vehicle-type-edit"
                  />
                </div>
              )}
            />

            <Controller
              name="vehicleModelId"
              control={control}
              render={({ field, fieldState }) => (
                <div className="flex flex-col gap-1.5">
                  <Typography variant="bodyLarge">
                    {t("label-vehicle-model")}{" "}
                    <span className="text-error-500">*</span>
                  </Typography>
                  <Select
                    options={vehicleModelOptions.map((vm) => ({
                      label: vm.name ?? "",
                      value: vm.id ?? "",
                    }))}
                    value={field.value}
                    placeholder={t("label-vehicle-model")}
                    onValueChange={(val) => field.onChange(val)}
                    error={fieldState.error}
                    data-testid="dropdown-operation-vehicle-model-edit"
                  />
                </div>
              )}
            />
          </div>

          {/* Vehicle Attachment (conditional) */}
          {showAttachment && (
            <Controller
              name="vehicleAttachmentIds"
              control={control}
              render={({ field }) => (
                <div className="flex flex-col gap-1.5">
                  <Typography variant="bodyLarge">
                    {t("label-vehicle-attachment")}
                  </Typography>
                  <Select
                    options={attachmentOptions.map((a) => ({
                      label: a.name,
                      value: a.id,
                      description: undefined,
                    }))}
                    value={field.value?.join(",") || ""}
                    placeholder={t("label-vehicle-attachment")}
                    onValueChange={(val) => {
                      const current = field.value || [];
                      const valStr = val as string;
                      if (current.includes(valStr)) {
                        field.onChange(current.filter((v) => v !== valStr));
                      } else {
                        field.onChange([...current, valStr]);
                      }
                    }}
                    data-testid="multiselect-operation-attachment-edit"
                  />
                  {field.value && field.value.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-1">
                      {field.value.map((attId) => {
                        const att = attachmentOptions.find((a) => a.id === attId);
                        return att ? (
                          <span
                            key={attId}
                            className="inline-flex items-center gap-1 rounded-full bg-primary-50 px-3 py-1 text-xs text-primary-700"
                          >
                            {att.name}
                            <button
                              type="button"
                              onClick={() =>
                                field.onChange(field.value.filter((v) => v !== attId))
                              }
                              className="ml-1 text-primary-500 hover:text-primary-700"
                              data-testid={`btn-remove-attachment-operation-edit-${attId}`}
                            >
                              <Icon name="close" size={12} />
                            </button>
                            <span className="text-greyScale-40 text-[10px]"></span>
                          </span>
                        ) : null;
                      })}
                    </div>
                  )}
                </div>
              )}
            />
          )}

          {/* Trip Type, Fuel Usage, Avg Speed - inline */}
          <div className="grid grid-cols-3 gap-4">
            <Controller
              name="tripTypeId"
              control={control}
              render={({ field, fieldState }) => (
                <div className="flex flex-col gap-1.5">
                  <Typography variant="bodyLarge">
                    {t("label-trip-type")}{" "}
                    <span className="text-error-500">*</span>
                  </Typography>
                  <Select
                    options={tripTypes.map((tt) => ({
                      label: tt.name ?? "",
                      value: tt.id ?? "",
                    }))}
                    value={field.value}
                    placeholder={t("label-trip-type")}
                    onValueChange={(val) => field.onChange(val)}
                    error={fieldState.error}
                    data-testid="dropdown-operation-trip-type-edit"
                  />
                </div>
              )}
            />

            <div className="flex flex-col gap-1.5">
              <Typography variant="bodyLarge">
                {t("label-fuel-usage")}{" "}
                <span className="text-error-500">*</span>
              </Typography>
              <Input
                type="number"
                placeholder="0"
                {...register("fuelUsage")}
                error={errors.fuelUsage}
                data-testid="input-operation-fuel-usage-edit"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <Typography variant="bodyLarge">
                {t("label-avg-speed")}{" "}
                <span className="text-error-500">*</span>
              </Typography>
              <Input
                type="number"
                placeholder="0"
                {...register("avgSpeed")}
                error={errors.avgSpeed}
                data-testid="input-operation-avg-speed-edit"
              />
            </div>
          </div>

          {/* Working Hours */}
          <div className="flex flex-col gap-1.5">
            <Typography variant="bodyLarge">
              Working Hours{" "}
              <span className="text-error-500">*</span>
            </Typography>
            <Input
              type="number"
              placeholder="0"
              {...register("workingHours")}
              error={errors.workingHours}
              data-testid="input-operation-working-hours-edit"
            />
          </div>
        </form>
      </section>

      {/* Bottom Action Bar */}
      <div className="flex justify-end gap-3">
        <Button
          type="button"
          variant="outline"
          color="tertiary"
          size="medium"
          onClick={() => setShowCancelModal(true)}
          data-testid="btn-cancel-operation-edit"
        >
          {t("btn-cancel")}
        </Button>
        <Button
          type="button"
          variant="fill"
          color="primary"
          size="medium"
          onClick={handleSubmit(onSubmit)}
          data-testid="btn-save-operation-edit"
        >
          {t("btn-save")}
        </Button>
      </div>

      {/* Save Confirmation Modal */}
      <ConfirmationModal
        open={showSaveModal}
        title={t("modal-save-title")}
        message={t("modal-save-message")}
        confirmLabel={t("modal-confirm-save")}
        cancelLabel={t("modal-confirm-cancel")}
        onConfirm={handleConfirmSave}
        onCancel={() => setShowSaveModal(false)}
        testId="save-vehicle-operation-edit-modal"
      />

      {/* Discard Confirmation Modal */}
      <ConfirmationModal
        open={showCancelModal}
        title={t("modal-discard-title")}
        message={t("modal-discard-message")}
        confirmLabel={t("modal-confirm-discard")}
        cancelLabel={t("modal-confirm-stay")}
        onConfirm={handleConfirmCancel}
        onCancel={() => setShowCancelModal(false)}
        testId="discard-vehicle-operation-edit-modal"
      />
    </div>
  );
};

export default EditVehicleOperationPage;
